import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Mail, Pencil } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { sitesService } from '../../../services/sitesService';
import { inspectionService } from '../../../services/inspectionService';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { QueryErrorBanner } from '../../../components/ui/query-error-banner';
import { useAuthStore } from '../../../stores/authStore';
import { editableInspectionId, forgetIfSent } from '../submittedDraft';
import { SendClock } from './SendClock';
import { inspectionTypeLabel } from '../../../utils/inspectionType';
import { ReportEmails } from '../../../components/reports/ReportEmails';

// Inspector-facing labels for inspection.status. Internally "submitted" is an
// inspection that is complete but whose report has not gone to the client, and
// "approved" is stamped by the send — so the badge says what the inspector
// actually did (Complete → Review → Send to Client, Chris 2026-09-04).
const INSPECTION_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  draft: 'Draft',
  submitted: 'Not Sent',
  approved: 'Sent',
  rejected: 'Rejected',
  completed: 'Completed',
};
function inspectionStatusLabel(status: string): string {
  return (
    INSPECTION_STATUS_LABELS[status] ??
    status.charAt(0).toUpperCase() + status.slice(1)
  );
}
const HOLD_POLL_MS = 15_000;

/** Complete, and its report is waiting out the ten-minute hold (JIM-1003-05). */
function isOnHold(inspection: {
  status: string;
  sendScheduledAt?: string | null;
}): boolean {
  return (
    inspection.status === 'submitted' && Boolean(inspection.sendScheduledAt)
  );
}

// Recent inspection reports. View resolves a fresh presigned URL via the same
// /report-url endpoint the hub Records tab uses. A report on its ten-minute
// hold shows the send clock where "Not Sent" would be, and the finished
// inspection this phone still holds the answers for gets an Edit button (Jim,
// Teams 2026-10-05: "add the clock showing pending send and the edit button
// here"). Edit stops the clock and reopens the inspection as it was left.
export function LiteSiteRecords({ siteId }: { siteId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);
  const [openingEditId, setOpeningEditId] = useState<string | null>(null);
  const [tooLateId, setTooLateId] = useState<string | null>(null);
  // JIM-1007-11: who a sent report went to, and Send again.
  const [emailsOpenId, setEmailsOpenId] = useState<string | null>(null);
  const {
    data: inspectionsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['lite-site-inspections', siteId],
    queryFn: () => sitesService.getSiteInspections(siteId, 10),
    enabled: !!siteId,
    retry: 1,
    // While a report is on its hold, check back so the row turns to Sent (or
    // back to Not Sent) without the inspector reloading.
    refetchInterval: (query) =>
      query.state.data?.inspections.some(isOnHold) ? HOLD_POLL_MS : false,
  });

  // The finished inspection whose answers are still on this phone.
  const editableKey = ['lite-editable-inspection', siteId, userId];
  const { data: editableId } = useQuery({
    queryKey: editableKey,
    queryFn: () => editableInspectionId(siteId, userId as string),
    enabled: !!siteId && !!userId,
  });

  // Once its report has gone, those answers are done with.
  const sentIds = (inspectionsData?.inspections ?? [])
    .filter((i) => i.status === 'approved')
    .map((i) => i.id)
    .join(',');
  useEffect(() => {
    if (!userId || !editableId || !sentIds.split(',').includes(editableId))
      return;
    forgetIfSent(siteId, userId, sentIds.split(','))
      .then((cleared) => {
        if (cleared)
          queryClient.invalidateQueries({
            queryKey: ['lite-editable-inspection', siteId, userId],
          });
      })
      .catch(() => {});
  }, [siteId, userId, editableId, sentIds, queryClient]);

  // Edit: stop the clock if the report is waiting to send, then reopen the
  // inspection. A send that has already started cannot be stopped.
  const handleEdit = async (inspection: {
    id: string;
    status: string;
    sendScheduledAt?: string | null;
  }) => {
    setOpeningEditId(inspection.id);
    setTooLateId(null);
    try {
      if (isOnHold(inspection))
        await inspectionService.cancelSend(inspection.id);
      navigate(`/lite/inspect/${siteId}?edit=${inspection.id}`);
    } catch {
      setTooLateId(inspection.id);
      refetch();
    } finally {
      setOpeningEditId(null);
    }
  };

  const handleViewReport = async (inspectionId: string) => {
    setLoadingReportId(inspectionId);
    try {
      const data = await inspectionService.getReportUrl(inspectionId);
      if (data.has_report && data.report_url) {
        window.open(data.report_url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get report URL:', err);
    } finally {
      setLoadingReportId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-4">
        <QueryErrorBanner error={error} onRetry={refetch} compact />
      </div>
    );
  }

  const inspections = inspectionsData?.inspections ?? [];
  if (inspections.length === 0) {
    return (
      <p className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400 italic">
        No inspections yet.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-700">
      {inspections.map((inspection) => {
        const dateStr = inspection.conductedDate || inspection.scheduledDate;
        const canEdit =
          inspection.status === 'submitted' && inspection.id === editableId;
        const sendState = isOnHold(inspection) ? (
          <SendClock sendsAt={inspection.sendScheduledAt as string} />
        ) : (
          <StatusBadge
            variant={
              inspection.status === 'approved' ||
              inspection.status === 'completed'
                ? 'success'
                : inspection.status === 'submitted'
                  ? 'info'
                  : 'neutral'
            }
            size="sm"
          >
            {inspectionStatusLabel(inspection.status)}
          </StatusBadge>
        );
        const sent = inspection.status === 'approved';
        const emailsOpen = emailsOpenId === inspection.id;
        return (
          <li key={inspection.id} className="px-4 py-2.5">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white whitespace-nowrap">
                  {dateStr ? format(parseISO(dateStr), 'MMM d, yyyy') : 'N/A'}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 capitalize truncate">
                  {inspectionTypeLabel(inspection.type)} ·{' '}
                  {inspection.inspectorName}
                </p>
                {/* With Edit and View both on the row there is no room beside
                  them on a phone: the clock goes under the name instead. */}
                {canEdit && <div className="mt-1">{sendState}</div>}
                {tooLateId === inspection.id && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Too late to edit: this report is already being sent.
                  </p>
                )}
              </div>
              {!canEdit && sendState}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleEdit(inspection)}
                  disabled={openingEditId === inspection.id}
                  aria-label={`Edit the inspection of ${
                    dateStr ? format(parseISO(dateStr), 'MMM d, yyyy') : 'N/A'
                  }`}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 flex-shrink-0"
                >
                  {openingEditId === inspection.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Pencil className="w-3.5 h-3.5" />
                  )}
                  Edit
                </button>
              )}
              {inspection.reportUrl && (
                <button
                  type="button"
                  onClick={() => handleViewReport(inspection.id)}
                  disabled={loadingReportId === inspection.id}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 disabled:opacity-50 flex-shrink-0"
                >
                  {loadingReportId === inspection.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FileText className="w-3.5 h-3.5" />
                  )}
                  View
                </button>
              )}
              {sent && (
                <button
                  type="button"
                  onClick={() =>
                    setEmailsOpenId(emailsOpen ? null : inspection.id)
                  }
                  aria-expanded={emailsOpen}
                  aria-label={`Emails for the inspection of ${
                    dateStr ? format(parseISO(dateStr), 'MMM d, yyyy') : 'N/A'
                  }`}
                  className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg flex-shrink-0 ${
                    emailsOpen
                      ? 'text-white bg-primary-600'
                      : 'text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <Mail className="w-3.5 h-3.5" />
                  Emails
                </button>
              )}
            </div>
            {emailsOpen && (
              <div className="mt-2 pl-1">
                <ReportEmails inspectionId={inspection.id} canSend />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
